import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuditLogsPage = () => {
  const { user } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/audit-logs`, { withCredentials: true });
      // API returns array directly
      setLogs(Array.isArray(response.data) ? response.data : response.data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      user_created: 'bg-green-50 text-green-700 border-green-200',
      user_updated: 'bg-blue-50 text-blue-700 border-blue-200',
      user_deactivated: 'bg-red-50 text-red-700 border-red-200',
      data_breach_created: 'bg-orange-50 text-orange-700 border-orange-200',
      breach_notifications_sent: 'bg-purple-50 text-purple-700 border-purple-200',
      backup_created: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      backup_restored: 'bg-indigo-50 text-indigo-700 border-indigo-200'
    };
    
    return (
      <Badge className={styles[action] || 'bg-slate-50 text-slate-700 border-slate-200'}>
        {action.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const filteredLogs = logs.filter(log => {
    if (filter !== 'all' && !log.action.includes(filter)) return false;
    if (search && !JSON.stringify(log).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return <div className="text-center py-12">A carregar...</div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-audit-logs-page">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Logs de Auditoria
        </h1>
        <p className="text-slate-600">
          Histórico completo de todas as ações críticas no sistema
        </p>
      </div>

      <div className="flex gap-4">
        <Input
          data-testid="search-logs-input"
          placeholder="Pesquisar logs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="filter-logs-select" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as Ações</SelectItem>
            <SelectItem value="user">Utilizadores</SelectItem>
            <SelectItem value="breach">Violações</SelectItem>
            <SelectItem value="backup">Backups</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {filteredLogs.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Activity className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Nenhum log encontrado</h3>
              <p className="text-slate-600">Não há logs que correspondam aos critérios.</p>
            </CardContent>
          </Card>
        ) : (
          filteredLogs.map((log, idx) => (
            <Card key={idx} className="border-slate-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <Activity className="w-5 h-5 text-slate-600 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      {getActionBadge(log.action)}
                      <span className="text-sm text-slate-600">
                        {new Date(log.timestamp).toLocaleString('pt-PT')}
                      </span>
                    </div>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="text-slate-600">Realizado por:</span>
                        <span className="ml-2 font-medium text-[#0F172A]">{log.performed_by}</span>
                      </div>
                      {log.target_user && (
                        <div>
                          <span className="text-slate-600">Utilizador alvo:</span>
                          <span className="ml-2 font-medium text-[#0F172A]">{log.target_user}</span>
                        </div>
                      )}
                      {log.details && (
                        <div>
                          <span className="text-slate-600">Detalhes:</span>
                          <pre className="ml-2 text-xs bg-slate-50 p-2 rounded mt-1 overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminAuditLogsPage;
